import { Building2, GripVertical, Plus, X } from "lucide-react";
import PropTypes from "prop-types";
import {
	BG_STYLE_OPTIONS,
	ICON_OPTIONS,
	PADDING_OPTIONS,
	SECTION_LAYOUT_OPTIONS,
	SECTION_TYPES,
	TEXT_ALIGN_OPTIONS,
} from "../constants/themes";
import { Input, Select, TextArea } from "./FormComponents";

/**
 * Renders the edit form for a single section.
 *
 * @param {Object} props
 * @param {Object} props.section - Section data
 * @param {number} props.index - Section index
 * @param {Function} props.onChange - (field, value) => void
 * @param {Function} props.onRemove - () => void
 * @param {boolean} [props.collapsed] - If true, show only header
 * @param {Function} [props.onToggle] - Toggle collapsed state
 */
// eslint-disable-next-line max-lines-per-function, complexity
export default function SectionEditor({
	section,
	index: _index,
	onChange,
	onRemove,
	collapsed,
	onToggle,
}) {
	const sectionType =
		SECTION_TYPES.find((t) => t.value === section.type) || SECTION_TYPES[1];
	const TypeIcon = sectionType.icon || Building2;

	const isCompanyIntro = section.type === "company_intro";
	const isCustom = section.type === "custom";
	const isFeaturesGrid = section.type === "features_grid";
	const isImageGallery = section.type === "image_gallery";
	const isCta = section.type === "cta";

	const borderColor = isCompanyIntro
		? "border-purple-500/20 bg-purple-500/5"
		: isCta
			? "border-cyan-500/20 bg-cyan-500/5"
			: "border-slate-700/50 bg-slate-800/30";

	// ── Header ──────────────────────────────────────────────────
	const header = (
		<div className="flex items-center justify-between select-none">
			<button
				type="button"
				className="flex-1 flex items-center gap-2 cursor-pointer text-left focus:outline-none focus:ring-1 focus:ring-cyan-500/30 rounded"
				onClick={onToggle}
				aria-expanded={!collapsed}
			>
				<GripVertical className="w-4 h-4 text-slate-600" />
				<TypeIcon className="w-4 h-4 text-slate-400" />
				<span className="text-xs font-semibold text-slate-300">
					{sectionType.label}
					{section.title ? ` — ${section.title}` : ""}
				</span>
			</button>
			<button
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					onRemove();
				}}
				className="text-slate-500 hover:text-red-400 transition-colors p-1"
				aria-label="Remover seção"
			>
				<X className="w-4 h-4" />
			</button>
		</div>
	);

	if (collapsed) {
		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>{header}</div>
		);
	}

	// ── Common style options (all types) ─────────────────────────
	const styleOptions = (
		<div className="grid grid-cols-3 gap-2 mt-2">
			<div>
				<span className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">
					Alinhamento
				</span>
				<div className="flex gap-1">
					{TEXT_ALIGN_OPTIONS.map((opt) => {
						const Icon = opt.icon;
						const active = (section.text_align || "left") === opt.value;
						return (
							<button
								key={opt.value}
								type="button"
								onClick={() => onChange("text_align", opt.value)}
								className={`flex-1 p-1.5 rounded border text-xs transition-all ${
									active
										? "bg-cyan-500/20 border-cyan-500/30 text-cyan-400"
										: "bg-slate-800/50 border-slate-700 text-slate-500 hover:text-slate-300"
								}`}
								title={opt.label}
							>
								<Icon className="w-3.5 h-3.5 mx-auto" />
							</button>
						);
					})}
				</div>
			</div>
			<Select
				label="Fundo"
				value={section.bg_style || "default"}
				onChange={(e) => onChange("bg_style", e.target.value)}
				options={BG_STYLE_OPTIONS}
			/>
			<Select
				label="Espacamento"
				value={section.padding || "md"}
				onChange={(e) => onChange("padding", e.target.value)}
				options={PADDING_OPTIONS}
			/>
		</div>
	);

	// ═══════════════════════════════════════════════════════════════
	// Company Intro
	// ═══════════════════════════════════════════════════════════════
	if (isCompanyIntro) {
		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>
				{header}
				<div className="mt-3 space-y-2">
					<div className="text-sm text-slate-500 italic p-3 bg-slate-900/50 rounded border border-dashed border-slate-600">
						<Building2 className="w-4 h-4 text-purple-400 inline mr-1 -mt-0.5" />
						Preenchido automaticamente com dados da empresa selecionada.
					</div>
					<div className="grid grid-cols-2 gap-2">
						<Input
							label="Titulo"
							value={section.title || "Quem Somos"}
							onChange={(e) => onChange("title", e.target.value)}
							placeholder="Quem Somos"
						/>
						<Input
							label="Badge"
							value={section.badge || "Sobre a Empresa"}
							onChange={(e) => onChange("badge", e.target.value)}
							placeholder="Sobre a Empresa"
						/>
					</div>
					{styleOptions}
				</div>
			</div>
		);
	}

	// ═══════════════════════════════════════════════════════════════
	// Custom (Text)
	// ═══════════════════════════════════════════════════════════════
	if (isCustom) {
		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>
				{header}
				<div className="mt-3 space-y-1">
					<Input
						label="Titulo"
						value={section.title || ""}
						onChange={(e) => onChange("title", e.target.value)}
						placeholder="Titulo da secao"
					/>
					<TextArea
						label="Conteudo"
						value={section.content || ""}
						onChange={(e) => onChange("content", e.target.value)}
						placeholder="Conteudo..."
						rows="3"
					/>
					<div className="grid grid-cols-2 gap-2">
						<Select
							label="Layout"
							value={section.layout || "full-width"}
							onChange={(e) => onChange("layout", e.target.value)}
							options={SECTION_LAYOUT_OPTIONS}
						/>
						<Select
							label="Icone"
							value={section.icon || ""}
							onChange={(e) => onChange("icon", e.target.value)}
							options={ICON_OPTIONS}
						/>
					</div>
					<div className="grid grid-cols-2 gap-2">
						<Input
							label="Badge (opcional)"
							value={section.badge || ""}
							onChange={(e) => onChange("badge", e.target.value)}
							placeholder="Ex: Nosso Diferencial"
						/>
						<Input
							label="URL da Imagem (opcional)"
							value={section.image_url || ""}
							onChange={(e) => onChange("image_url", e.target.value)}
							placeholder="https://..."
						/>
					</div>
					{styleOptions}
				</div>
			</div>
		);
	}

	// ═══════════════════════════════════════════════════════════════
	// Features Grid
	// ═══════════════════════════════════════════════════════════════
	if (isFeaturesGrid) {
		const cards = section.cards || [];

		const handleAddCard = () => {
			onChange("cards", [...cards, { icon: "star", title: "", content: "" }]);
		};

		const handleCardChange = (ci, field, value) => {
			const updated = [...cards];
			updated[ci] = { ...updated[ci], [field]: value };
			onChange("cards", updated);
		};

		const handleRemoveCard = (ci) => {
			onChange(
				"cards",
				cards.filter((_, i) => i !== ci),
			);
		};

		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>
				{header}
				<div className="mt-3 space-y-1">
					<div className="grid grid-cols-2 gap-2">
						<Input
							label="Titulo"
							value={section.title || ""}
							onChange={(e) => onChange("title", e.target.value)}
							placeholder="Ex: Nossos Diferenciais"
						/>
						<Input
							label="Badge (opcional)"
							value={section.badge || ""}
							onChange={(e) => onChange("badge", e.target.value)}
							placeholder="Ex: Por que escolher"
						/>
					</div>
					<div className="grid grid-cols-2 gap-2">
						<Select
							label="Icone"
							value={section.icon || ""}
							onChange={(e) => onChange("icon", e.target.value)}
							options={ICON_OPTIONS}
						/>
						<Select
							label="Colunas"
							value={String(section.columns || 3)}
							onChange={(e) =>
								onChange("columns", parseInt(e.target.value, 10))
							}
							options={[
								{ value: "2", label: "2 Colunas" },
								{ value: "3", label: "3 Colunas" },
								{ value: "4", label: "4 Colunas" },
							]}
						/>
					</div>

					{/* Cards list */}
					<div className="border-t border-slate-700/50 pt-2 mt-2">
						<div className="flex items-center justify-between mb-2">
							<span className="text-xs font-semibold text-slate-400">
								Cards ({cards.length})
							</span>
							<button
								type="button"
								onClick={handleAddCard}
								className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
							>
								<Plus className="w-3 h-3" /> Adicionar Card
							</button>
						</div>
						{cards.map((card, ci) => (
							<div
								key={card.id || `card-${ci}`}
								className="p-2 bg-slate-900/50 rounded border border-slate-700/30 mb-2"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="text-[10px] text-slate-500">
										Card {ci + 1}
									</span>
									<button
										type="button"
										onClick={() => handleRemoveCard(ci)}
										className="text-slate-500 hover:text-red-400"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								<div className="grid grid-cols-[80px_1fr] gap-2">
									<Select
										label="Icone"
										value={card.icon || ""}
										onChange={(e) =>
											handleCardChange(ci, "icon", e.target.value)
										}
										options={ICON_OPTIONS}
									/>
									<Input
										label="Titulo"
										value={card.title || ""}
										onChange={(e) =>
											handleCardChange(ci, "title", e.target.value)
										}
										placeholder="Titulo do card"
									/>
								</div>
								<Input
									label="Descricao"
									value={card.content || ""}
									onChange={(e) =>
										handleCardChange(ci, "content", e.target.value)
									}
									placeholder="Descricao curta..."
								/>
							</div>
						))}
					</div>

					{styleOptions}
				</div>
			</div>
		);
	}

	// ═══════════════════════════════════════════════════════════════
	// Image Gallery
	// ═══════════════════════════════════════════════════════════════
	if (isImageGallery) {
		const images = section.images || [];

		const handleAddImage = () => {
			onChange("images", [...images, { url: "", caption: "" }]);
		};

		const handleImageChange = (ii, field, value) => {
			const updated = [...images];
			updated[ii] = { ...updated[ii], [field]: value };
			onChange("images", updated);
		};

		const handleRemoveImage = (ii) => {
			onChange(
				"images",
				images.filter((_, i) => i !== ii),
			);
		};

		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>
				{header}
				<div className="mt-3 space-y-1">
					<div className="grid grid-cols-2 gap-2">
						<Input
							label="Titulo"
							value={section.title || ""}
							onChange={(e) => onChange("title", e.target.value)}
							placeholder="Ex: Nossos Cases"
						/>
						<Input
							label="Badge (opcional)"
							value={section.badge || ""}
							onChange={(e) => onChange("badge", e.target.value)}
							placeholder="Ex: Portfolio"
						/>
					</div>
					<Select
						label="Colunas"
						value={String(section.columns || 2)}
						onChange={(e) => onChange("columns", parseInt(e.target.value, 10))}
						options={[
							{ value: "1", label: "1 Coluna" },
							{ value: "2", label: "2 Colunas" },
							{ value: "3", label: "3 Colunas" },
						]}
					/>

					{/* Images list */}
					<div className="border-t border-slate-700/50 pt-2 mt-2">
						<div className="flex items-center justify-between mb-2">
							<span className="text-xs font-semibold text-slate-400">
								Imagens ({images.length})
							</span>
							<button
								type="button"
								onClick={handleAddImage}
								className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
							>
								<Plus className="w-3 h-3" /> Adicionar Imagem
							</button>
						</div>
						{images.map((img, ii) => (
							<div
								key={img.id || `img-${ii}`}
								className="p-2 bg-slate-900/50 rounded border border-slate-700/30 mb-2"
							>
								<div className="flex items-center justify-between mb-1">
									<span className="text-[10px] text-slate-500">
										Imagem {ii + 1}
									</span>
									<button
										type="button"
										onClick={() => handleRemoveImage(ii)}
										className="text-slate-500 hover:text-red-400"
									>
										<X className="w-3 h-3" />
									</button>
								</div>
								<Input
									label="URL"
									value={img.url || ""}
									onChange={(e) => handleImageChange(ii, "url", e.target.value)}
									placeholder="https://..."
								/>
								<Input
									label="Legenda (opcional)"
									value={img.caption || ""}
									onChange={(e) =>
										handleImageChange(ii, "caption", e.target.value)
									}
									placeholder="Legenda da imagem"
								/>
								{img.url && (
									<div className="mt-1 rounded border border-white/5 overflow-hidden bg-slate-800/50 max-h-24">
										<img
											src={img.url}
											alt={img.caption || ""}
											className="w-full h-full object-cover"
										/>
									</div>
								)}
							</div>
						))}
					</div>

					{styleOptions}
				</div>
			</div>
		);
	}

	// ═══════════════════════════════════════════════════════════════
	// CTA / Destaque
	// ═══════════════════════════════════════════════════════════════
	if (isCta) {
		return (
			<div className={`p-3 rounded-lg border ${borderColor}`}>
				{header}
				<div className="mt-3 space-y-1">
					<Input
						label="Titulo"
						value={section.title || ""}
						onChange={(e) => onChange("title", e.target.value)}
						placeholder="Ex: Pronto para comecar?"
					/>
					<TextArea
						label="Conteudo"
						value={section.content || ""}
						onChange={(e) => onChange("content", e.target.value)}
						placeholder="Texto do CTA..."
						rows="2"
					/>
					<div className="grid grid-cols-2 gap-2">
						<Input
							label="Badge (opcional)"
							value={section.badge || ""}
							onChange={(e) => onChange("badge", e.target.value)}
							placeholder="Ex: Proximo Passo"
						/>
						<Select
							label="Icone"
							value={section.icon || ""}
							onChange={(e) => onChange("icon", e.target.value)}
							options={ICON_OPTIONS}
						/>
					</div>
					{styleOptions}
				</div>
			</div>
		);
	}

	// Fallback (unknown type) — render as custom
	return (
		<div className={`p-3 rounded-lg border ${borderColor}`}>
			{header}
			<div className="mt-3">
				<p className="text-xs text-slate-500 italic">
					Tipo de secao desconhecido: {section.type}
				</p>
			</div>
		</div>
	);
}

SectionEditor.propTypes = {
	section: PropTypes.shape({
		type: PropTypes.string.isRequired,
		title: PropTypes.string,
		badge: PropTypes.string,
		content: PropTypes.string,
		layout: PropTypes.string,
		icon: PropTypes.string,
		image_url: PropTypes.string,
		text_align: PropTypes.string,
		bg_style: PropTypes.string,
		padding: PropTypes.string,
		columns: PropTypes.number,
		cards: PropTypes.arrayOf(
			PropTypes.shape({
				icon: PropTypes.string,
				title: PropTypes.string,
				content: PropTypes.string,
			}),
		),
		images: PropTypes.arrayOf(
			PropTypes.shape({
				url: PropTypes.string,
				caption: PropTypes.string,
			}),
		),
	}).isRequired,
	index: PropTypes.number,
	onChange: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
	collapsed: PropTypes.bool,
	onToggle: PropTypes.func,
};
